import type { FormEventHandler } from 'react';
import { Link } from 'react-router-dom';
import type { KidsChildAccessRequest } from './types';

type ChildRequestsController = {
  actionMessage: string;
  actionState: 'idle' | 'saving';
  handleApprove: (request: KidsChildAccessRequest) => void;
  handleDecline: (requestId: string) => void;
  requests: KidsChildAccessRequest[];
  state: 'loading' | 'synced' | 'fallback';
};

type SectionHeaderComponent = (props: { eyebrow: string; title: string; text?: string }) => JSX.Element;
type ParentSyncState = 'loading' | 'synced' | 'local';
type SyncNoticeComponent = (props: { state: ParentSyncState; label: string }) => JSX.Element;
type DashboardHeaderComponent = (props: { title: string; text: string }) => JSX.Element;
type StatsGridComponent = (props: { stats: string[][] }) => JSX.Element;
type ActivityCardComponent = (props: { title: string; text: string; to: string; tone: string }) => JSX.Element;
type AdminTablePageComponent = (props: {
  eyebrow: string;
  title: string;
  rows: string[][];
  actionTo: string;
  actionLabel: string;
  compact?: boolean;
  rowActionLabel?: string;
}) => JSX.Element;
type SaveNoticeComponent = (props: { state: 'idle' | 'saving' | 'synced' | 'local' | 'error'; error: string }) => JSX.Element | null;

export function DashboardHeader({ title, text }: { title: string; text: string }) {
  return (
    <header className="dashboard-header">
      <div>
        <p className="eyebrow">Alhudah Kids</p>
        <h1>{title}</h1>
        <p>{text}</p>
      </div>
      <Link className="button button-secondary" to="/kids/parent-guide">
        Parent Guide
      </Link>
    </header>
  );
}

export function StatsGrid({ stats }: { stats: string[][] }) {
  return (
    <div className="stats-grid">
      {stats.map(([label, value]) => (
        <article className="stat-card" key={label}>
          <span>{label}</span>
          <strong>{value}</strong>
        </article>
      ))}
    </div>
  );
}

export function ActivityCard({ title, text, to, tone }: { title: string; text: string; to: string; tone: string }) {
  return (
    <Link className={`activity-card ${tone}`} to={to}>
      <strong>{title}</strong>
      <p>{text}</p>
    </Link>
  );
}

export function SectionHeader({ eyebrow, title, text }: { eyebrow: string; title: string; text?: string }) {
  return (
    <div className="section-header">
      <p className="eyebrow">{eyebrow}</p>
      <h2>{title}</h2>
      {text ? <p>{text}</p> : null}
    </div>
  );
}

export function AdminTablePage({
  eyebrow,
  title,
  rows,
  actionTo,
  actionLabel,
  compact,
  rowActionLabel = 'Edit',
}: {
  eyebrow: string;
  title: string;
  rows: string[][];
  actionTo: string;
  actionLabel: string;
  compact?: boolean;
  rowActionLabel?: string;
}) {
  return (
    <section className={compact ? 'admin-table-section compact' : 'admin-table-section'}>
      <div className="title-row">
        <SectionHeader eyebrow={eyebrow} title={title} />
        <Link className="button button-primary" to={actionTo}>
          {actionLabel}
        </Link>
      </div>
      <div className="table-card admin-table">
        {rows.map((row) => (
          <div className="table-row" key={row.join('-')}>
            {row.map((cell) => (
              <span key={cell}>{cell}</span>
            ))}
            <Link to={actionTo}>{rowActionLabel}</Link>
          </div>
        ))}
      </div>
    </section>
  );
}

export function SaveNotice({ state, error }: { state: 'idle' | 'saving' | 'synced' | 'local' | 'error'; error?: string }) {
  if (state === 'synced') return <p className="form-success">Saved to Supabase.</p>;
  if (state === 'local') return <p className="form-note">Validated locally. Supabase sync is pending configuration or admin access.</p>;
  if (state === 'error') return <p className="field-error">{error ?? 'Unable to save.'}</p>;
  return null;
}

export function ManageChildrenView({
  state,
  rows,
  childRequests,
  parentChildrenFallback,
  SectionHeader,
  SyncNotice,
}: {
  state: ParentSyncState;
  rows: string[][] | null;
  childRequests: ChildRequestsController;
  parentChildrenFallback: string[][];
  SectionHeader: SectionHeaderComponent;
  SyncNotice: SyncNoticeComponent;
}) {
  return (
    <section>
      <div className="title-row">
        <SectionHeader eyebrow="Manage Children" title="Profiles, goals, and safety settings." />
        <Link className="button button-primary" to="/kids/onboarding/profile">
          Add Child
        </Link>
      </div>
      <SyncNotice label="Children" state={state} />
      <section className="parent-command-panel compact">
        <div>
          <p className="eyebrow">Child Gmail requests</p>
          <h2>Approve children who asked to join with Gmail.</h2>
          <p>Approving creates a parent-managed child profile. Children are not added until a parent approves.</p>
        </div>
        <Link className="button button-secondary" to="/kids/request-parent">
          Child Request Form
        </Link>
      </section>
      {childRequests.actionMessage ? (
        <p className={childRequests.state === 'fallback' ? 'form-note' : 'form-success'}>
          {childRequests.actionMessage}
        </p>
      ) : null}
      {childRequests.state === 'loading' ? <p className="form-note">Loading child requests...</p> : null}
      {childRequests.state === 'fallback' ? <p className="form-note">Sign in as a parent to review child access requests.</p> : null}
      {childRequests.requests.length ? (
        <div className="children-table">
          {childRequests.requests.map((request) => (
            <article key={request.id}>
              <div className="profile-avatar-large small">{request.child_name.slice(0, 1)}</div>
              <div>
                <strong>{request.child_name}</strong>
                <span>{request.child_email} · {request.age_group_slug.replace(/-/g, ' ')}</span>
              </div>
              <span>{request.status}</span>
              <span>{new Date(request.created_at).toLocaleDateString()}</span>
              <em>{request.message || 'Ready for parent approval'}</em>
              {request.status === 'pending' ? (
                <div className="button-row compact-actions">
                  <button className="button button-primary" disabled={childRequests.actionState === 'saving'} onClick={() => childRequests.handleApprove(request)} type="button">
                    Approve
                  </button>
                  <button className="button button-secondary" disabled={childRequests.actionState === 'saving'} onClick={() => childRequests.handleDecline(request.id)} type="button">
                    Decline
                  </button>
                </div>
              ) : request.approved_profile_id ? (
                <Link to={`/kids/parent/children/${request.approved_profile_id}`}>View Profile</Link>
              ) : (
                <span>Closed</span>
              )}
            </article>
          ))}
        </div>
      ) : childRequests.state === 'synced' ? (
        <p className="form-success">No child Gmail requests are waiting right now.</p>
      ) : null}
      <div className="children-table">
        {(rows ?? parentChildrenFallback.map((row) => ['', ...row])).map(([id, name, age, avatar, level, goal, status]) => (
          <article key={id || name}>
            <div className="profile-avatar-large small">{name.slice(0, 1)}</div>
            <div>
              <strong>{name}</strong>
              <span>{age} · {avatar}</span>
            </div>
            <span>{level}</span>
            <span>{goal}</span>
            <em>{status}</em>
            <Link to={id ? `/kids/parent/children/${id}` : '/kids/onboarding/path'}>{id ? 'View' : 'Finish setup'}</Link>
          </article>
        ))}
      </div>
      <section className="parent-command-panel compact">
        <div>
          <p className="eyebrow">Parent controls</p>
          <h2>Each child has their own age group, avatar, path, goal, and safety settings.</h2>
        </div>
        <Link className="button button-secondary" to="/kids/parent/settings">
          Manage Settings
        </Link>
      </section>
    </section>
  );
}

export function ParentDashboardView({
  state,
  stats,
  firstProfileId,
  firstProfileName,
  latestAttemptSummary,
  completedLessons,
  totalQuestions,
  activeDays,
  hasSettings,
  recommendationCards,
  DashboardHeader,
  SyncNotice,
  StatsGrid,
  ActivityCard,
}: {
  state: ParentSyncState;
  stats: string[][];
  firstProfileId: string | null;
  firstProfileName: string | null;
  latestAttemptSummary: {
    title: string;
    correctCount: number;
    totalQuestions: number;
    starsEarned: number;
  } | null;
  completedLessons: number;
  totalQuestions: number;
  activeDays: number;
  hasSettings: boolean;
  recommendationCards: string[][];
  DashboardHeader: DashboardHeaderComponent;
  SyncNotice: SyncNoticeComponent;
  StatsGrid: StatsGridComponent;
  ActivityCard: ActivityCardComponent;
}) {
  return (
    <section>
      <DashboardHeader title="Assalamu Alaikum, Parent" text="Track progress and guide the next learning step." />
      <SyncNotice label="Parent progress" state={state} />
      <StatsGrid stats={stats} />
      <section className="parent-command-panel">
        <div>
          <p className="eyebrow">This week</p>
          <h2>{firstProfileName ? `${firstProfileName} is building a Quran learning habit.` : 'Create a child profile to begin tracking progress.'}</h2>
          <p>
            {latestAttemptSummary
              ? `${latestAttemptSummary.title} was completed with ${latestAttemptSummary.correctCount}/${latestAttemptSummary.totalQuestions} correct.`
              : completedLessons
                ? `${completedLessons} lesson${completedLessons === 1 ? '' : 's'} completed. Review the newest lesson together.`
                : 'Once a child completes a quiz or lesson, this dashboard will show strengths, stars, XP, and next steps.'}
          </p>
        </div>
        <div className="parent-checklist">
          <span className="done">Parent account active</span>
          <span className={firstProfileName ? 'done' : ''}>Child profile active</span>
          <span className={hasSettings ? 'done' : ''}>Daily goal set</span>
          <span>{activeDays ? `${activeDays} active day${activeDays === 1 ? '' : 's'}` : 'First active day pending'}</span>
        </div>
      </section>
      <div className="dashboard-grid">
        <ActivityCard
          title="Recent Activity"
          text={latestAttemptSummary ? `${latestAttemptSummary.title} completed for ${latestAttemptSummary.starsEarned} stars.` : 'No completed quiz yet.'}
          to={firstProfileId ? `/kids/parent/children/${firstProfileId}` : '/kids/onboarding/profile'}
          tone="green"
        />
        <ActivityCard title="Recommended Next" text={totalQuestions || completedLessons ? 'Review the latest learning together.' : 'Start with a short lesson.'} to="/kids/app/learn" tone="blue" />
        <ActivityCard title="Safety Settings" text="Manage profiles, language, reminders, and leaderboard visibility." to="/kids/parent/settings" tone="yellow" />
      </div>
      <section className="insight-grid">
        {recommendationCards.map(([title, text]) => (
          <article key={title}>
            <strong>{title}</strong>
            <p>{text}</p>
          </article>
        ))}
      </section>
    </section>
  );
}

export function ChildProgressView({
  state,
  profileName,
  levelNumber,
  totalStars,
  completedLessonsCount,
  accuracy,
  latestAttemptSummary,
  latestAttemptXp,
  xp,
  tableRows,
  recommendationCards,
  recentLessons,
  DashboardHeader,
  SyncNotice,
  StatsGrid,
}: {
  state: ParentSyncState;
  profileName: string;
  levelNumber: number;
  totalStars: number;
  completedLessonsCount: number;
  accuracy: string;
  latestAttemptSummary: {
    title: string;
    starsEarned: number;
  } | null;
  latestAttemptXp: number;
  xp: number;
  tableRows: string[][];
  recommendationCards: string[][];
  recentLessons: { id: string; title: string; progressPercent: number }[];
  DashboardHeader: DashboardHeaderComponent;
  SyncNotice: SyncNoticeComponent;
  StatsGrid: StatsGridComponent;
}) {
  return (
    <section>
      <DashboardHeader title={`${profileName} progress detail.`} text="A clear parent view of strengths, practice needs, and next steps." />
      <SyncNotice label="Child progress" state={state} />
      <StatsGrid
        stats={[
          ['Level', `${levelNumber}`],
          ['Stars', `${totalStars}`],
          ['Lessons', `${completedLessonsCount}`],
          ['Accuracy', accuracy],
        ]}
      />
      <section className="progress-detail">
        <div>
          <p className="eyebrow">Learning health</p>
          <h2>{latestAttemptSummary ? `${latestAttemptSummary.title} is the latest completed activity.` : 'Ready for the first quiz.'}</h2>
          <p>
            {latestAttemptSummary
              ? `${profileName} earned ${latestAttemptSummary.starsEarned} stars and ${latestAttemptXp} XP. Review one explanation together to reinforce learning.`
              : 'Start with a short quiz, then return here to see progress and parent recommendations.'}
          </p>
          <div className="skill-bars">
            <label>Quiz accuracy<span><b style={{ width: accuracy }} /></span></label>
            <label>Stars earned<span><b style={{ width: `${Math.min(100, totalStars)}%` }} /></span></label>
            <label>XP progress<span><b style={{ width: `${Math.min(100, xp)}%` }} /></span></label>
          </div>
        </div>
        <div className="progress-notes">
          <strong>Parent note</strong>
          <p>Ask {profileName} to explain one answer after each quiz. If they can teach it back, the topic is becoming familiar.</p>
        </div>
      </section>
      <div className="table-card progress-table">
        {tableRows.map((row) => (
          <div className="table-row" key={row.join('-')}>
            {row.map((cell) => (
              <span key={cell}>{cell}</span>
            ))}
            <Link to="/kids/app/quizzes">Review</Link>
          </div>
        ))}
      </div>
      <section className="progress-detail">
        <div>
          <p className="eyebrow">Lesson progress</p>
          <h2>{completedLessonsCount ? `${completedLessonsCount} lesson${completedLessonsCount === 1 ? '' : 's'} completed.` : 'No completed lessons yet.'}</h2>
          <p>Completed lessons help parents see whether Quran learning is happening before quiz practice.</p>
        </div>
        <div className="progress-notes">
          <strong>Recent lessons</strong>
          {recentLessons.length ? (
            <ul className="lesson-list">
              {recentLessons.map((lesson) => (
                <li key={lesson.id}>{lesson.title} · {lesson.progressPercent}%</li>
              ))}
            </ul>
          ) : (
            <p>Ask your child to complete one short Knowledge Base lesson.</p>
          )}
        </div>
      </section>
      <section className="insight-grid">
        {recommendationCards.map(([title, text]) => (
          <article key={title}>
            <strong>{title}</strong>
            <p>{text}</p>
          </article>
        ))}
      </section>
    </section>
  );
}

export function ParentSettingsView({
  values,
  errors,
  status,
  onChange,
  onSubmit,
}: {
  values: {
    parent_name: string;
    email: string;
    language: string;
    notifications: string;
    leaderboard: string;
    message: string;
  };
  errors: Record<string, string>;
  status: 'idle' | 'saving' | 'sent' | 'local';
  onChange: FormEventHandler<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>;
  onSubmit: FormEventHandler<HTMLFormElement>;
}) {
  return (
    <section className="auth-layout">
      <form className="auth-card wide" onSubmit={onSubmit}>
        <p className="eyebrow">Parent Settings</p>
        <h1>Account, privacy, language, and notifications.</h1>
        <div className="form-grid">
          <label>
            Parent name
            <input name="parent_name" onChange={onChange} value={values.parent_name} />
            {errors.parent_name ? <span className="field-error">{errors.parent_name}</span> : null}
          </label>
          <label>
            Email
            <input name="email" onChange={onChange} type="email" value={values.email} />
            {errors.email ? <span className="field-error">{errors.email}</span> : null}
          </label>
          <label>
            Language
            <select name="language" onChange={onChange} value={values.language}>
              <option>English</option>
              <option>Arabic</option>
              <option>Urdu</option>
            </select>
          </label>
          <label>
            Notifications
            <select name="notifications" onChange={onChange} value={values.notifications}>
              <option>Weekly summary</option>
              <option>Daily reminder</option>
              <option>Important updates only</option>
            </select>
          </label>
          <label>
            Leaderboard privacy
            <select name="leaderboard" onChange={onChange} value={values.leaderboard}>
              <option>Avatar and nickname only</option>
              <option>Friends only</option>
              <option>Hide from leaderboard</option>
            </select>
          </label>
          <label>
            Request a change
            <textarea
              name="message"
              onChange={onChange}
              placeholder="Tell us what you want changed in quizzes, content, settings, or child experience."
              value={values.message}
            />
            {errors.message ? <span className="field-error">{errors.message}</span> : null}
          </label>
        </div>
        {status === 'sent' ? <p className="form-success">Settings saved and your request was sent for review.</p> : null}
        {status === 'local' ? <p className="form-note">Settings validated locally. Backend sync will run when Supabase is configured.</p> : null}
        <button className="button button-primary full" disabled={status === 'saving'} type="submit">
          {status === 'saving' ? 'Saving...' : 'Save Settings'}
        </button>
      </form>
    </section>
  );
}

export function AdminLoginView({
  authMode,
  values,
  status,
  error,
  onChange,
  onSubmit,
  onGoogleSignIn,
  onMagicLink,
  onSetAuthMode,
}: {
  authMode: 'magic' | 'password';
  values: { email: string; password: string };
  status: 'idle' | 'checking' | 'denied' | 'error' | 'magic-link';
  error: string;
  onChange: FormEventHandler<HTMLInputElement>;
  onSubmit: FormEventHandler<HTMLFormElement>;
  onGoogleSignIn: () => void;
  onMagicLink: () => void;
  onSetAuthMode: (mode: 'magic' | 'password') => void;
}) {
  const isChecking = status === 'checking';

  return (
    <section className="auth-layout admin-login">
      <form className="auth-card" onSubmit={onSubmit} noValidate>
        <p className="eyebrow">Kids CMS</p>
        <h1>Kids CMS access.</h1>
        <p>Approved content managers can manage quizzes, lessons, media, awards, rewards, reports, and moderation.</p>
        <button className="button button-secondary full" disabled={status === 'checking'} onClick={onGoogleSignIn} type="button">
          {status === 'checking' ? 'Opening Google...' : 'Continue with Google'}
        </button>
        <div className="auth-divider" role="separator">
          <span>or use email</span>
        </div>
        <label>
          Work email
          <input name="email" onChange={onChange} placeholder="admin@example.com" type="email" value={values.email} />
        </label>
        {authMode === 'magic' ? (
          <button className="button button-primary full" disabled={!values.email || isChecking} onClick={onMagicLink} type="button">
            {isChecking ? 'Sending...' : 'Send Magic Link'}
          </button>
        ) : (
          <>
            <label>
              Password
              <input name="password" onChange={onChange} placeholder="Password" type="password" value={values.password} />
            </label>
            <button className="button button-primary full" disabled={isChecking} type="submit">
              {isChecking ? 'Checking...' : 'Open Kids CMS'}
            </button>
          </>
        )}
        <button className="cms-auth-toggle" disabled={isChecking} onClick={() => onSetAuthMode(authMode === 'magic' ? 'password' : 'magic')} type="button">
          {authMode === 'magic' ? 'Sign in with password instead' : 'Use magic link instead'}
        </button>
        {status === 'magic-link' ? <p className="form-success">Magic link requested. Check your email, then return to Kids CMS.</p> : null}
        {status === 'denied' ? <p className="form-note">This account is not authorized for Kids CMS.</p> : null}
        {status === 'error' ? <p className="field-error">{error || 'Unable to sign in.'}</p> : null}
        <p className="form-note">All CMS actions are protected by Supabase access rules and audit logging.</p>
      </form>
    </section>
  );
}

export function AdminAuditLogView({
  state,
  stats,
  latestActivityTitle,
  latestActivityText,
  entityCount,
  rows,
  DashboardHeader,
  SyncNotice,
  StatsGrid,
  AdminTablePage,
}: {
  state: ParentSyncState;
  stats: string[][];
  latestActivityTitle: string;
  latestActivityText: string;
  entityCount: number;
  rows: string[][];
  DashboardHeader: DashboardHeaderComponent;
  SyncNotice: SyncNoticeComponent;
  StatsGrid: StatsGridComponent;
  AdminTablePage: AdminTablePageComponent;
}) {
  return (
    <section>
      <DashboardHeader title="Admin audit trail." text="Review recent CMS, media, quiz, and moderation changes made by admin users." />
      <SyncNotice label="Audit log" state={state} />
      <StatsGrid stats={stats} />
      <section className="admin-command-panel">
        <div>
          <p className="eyebrow">Latest activity</p>
          <h2>{latestActivityTitle}</h2>
          <p>{latestActivityText}</p>
        </div>
        <div className="stat-pill">
          <strong>{entityCount}</strong>
          <span>entity types</span>
        </div>
      </section>
      <AdminTablePage
        eyebrow="Audit"
        title="Recent admin changes."
        rows={rows}
        actionTo="/kids/admin/audit"
        actionLabel="Refresh"
        compact
        rowActionLabel="View"
      />
    </section>
  );
}

export function AdminReportsView({
  state,
  stats,
  lowAccuracyText,
  strongPerformerText,
  rewardImpactText,
  rows,
  DashboardHeader,
  SyncNotice,
  StatsGrid,
  AdminTablePage,
}: {
  state: ParentSyncState;
  stats: string[][];
  lowAccuracyText: string;
  strongPerformerText: string;
  rewardImpactText: string;
  rows: string[][];
  DashboardHeader: DashboardHeaderComponent;
  SyncNotice: SyncNoticeComponent;
  StatsGrid: StatsGridComponent;
  AdminTablePage: AdminTablePageComponent;
}) {
  return (
    <section>
      <DashboardHeader title="Quiz performance and content health." text="Use completion, accuracy, attempts, and feedback to improve learning content." />
      <SyncNotice label="Reports" state={state} />
      <StatsGrid stats={stats} />
      <AdminTablePage
        eyebrow="Reports"
        title="Content performance by quiz."
        rows={rows}
        actionTo="/kids/admin/moderation"
        actionLabel="Review Issues"
        compact
      />
      <section className="insight-grid">
        <article><strong>Low accuracy signal</strong><p>{lowAccuracyText}</p></article>
        <article><strong>Strong performer</strong><p>{strongPerformerText}</p></article>
        <article><strong>Reward impact</strong><p>{rewardImpactText}</p></article>
      </section>
    </section>
  );
}

export function ModerationReviewView({
  state,
  stats,
  rows,
  selectedRequestId,
  requestOptions,
  selectedRequestTitle,
  selectedRequestMessage,
  selectedRequestContext,
  reviewStatus,
  reviewerNotes,
  saveState,
  saveError,
  onSelectedRequestChange,
  onReviewChange,
  onSubmit,
  DashboardHeader,
  SyncNotice,
  StatsGrid,
  AdminTablePage,
  SaveNotice,
}: {
  state: ParentSyncState;
  stats: string[][];
  rows: string[][];
  selectedRequestId: string;
  requestOptions: Array<{ id: string; label: string }>;
  selectedRequestTitle: string;
  selectedRequestMessage: string;
  selectedRequestContext: string | null;
  reviewStatus: string;
  reviewerNotes: string;
  saveState: 'idle' | 'saving' | 'synced' | 'local' | 'error';
  saveError: string;
  onSelectedRequestChange: FormEventHandler<HTMLSelectElement>;
  onReviewChange: FormEventHandler<HTMLSelectElement | HTMLTextAreaElement>;
  onSubmit: FormEventHandler<HTMLFormElement>;
  DashboardHeader: DashboardHeaderComponent;
  SyncNotice: SyncNoticeComponent;
  StatsGrid: StatsGridComponent;
  AdminTablePage: AdminTablePageComponent;
  SaveNotice: SaveNoticeComponent;
}) {
  return (
    <section>
      <DashboardHeader title="Review feedback and content issues." text="Triage parent feedback, quiz issues, accessibility notes, and content corrections." />
      <SyncNotice label="Moderation queue" state={state} />
      <StatsGrid stats={stats} />
      <AdminTablePage eyebrow="Quality Hub" title="Moderation queue." rows={rows} actionTo="/kids/admin/moderation" actionLabel="Assign Reviewer" compact />
      <section className="moderation-panel">
        <div>
          <p className="eyebrow">Review action</p>
          <h2>{selectedRequestTitle}</h2>
          <p>{selectedRequestMessage}</p>
          {selectedRequestContext ? <p className="form-note">Context: {selectedRequestContext}</p> : null}
        </div>
        <form className="support-form" onSubmit={onSubmit}>
          <label>
            Request
            <select disabled={!requestOptions.length || saveState === 'saving'} onChange={onSelectedRequestChange} value={selectedRequestId}>
              {requestOptions.length ? null : <option value="">No requests</option>}
              {requestOptions.map((request) => (
                <option key={request.id} value={request.id}>
                  {request.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Status
            <select disabled={!selectedRequestId || saveState === 'saving'} name="status" onChange={onReviewChange} value={reviewStatus}>
              <option value="new">New</option>
              <option value="in_review">In review</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </label>
          <label>
            Reviewer notes
            <textarea
              disabled={!selectedRequestId || saveState === 'saving'}
              name="reviewer_notes"
              onChange={onReviewChange}
              placeholder="Record the action taken, follow-up needed, or why this request was closed."
              value={reviewerNotes}
            />
          </label>
          <SaveNotice error={saveError} state={saveState} />
          <button className="button button-primary" disabled={!selectedRequestId || saveState === 'saving'} type="submit">
            {saveState === 'saving' ? 'Saving...' : 'Save Review'}
          </button>
        </form>
      </section>
    </section>
  );
}

export function MediaLibraryView({
  state,
  values,
  errors,
  saveState,
  saveError,
  hasSelectedFile,
  displayedRows,
  onChange,
  onFileChange,
  onSubmit,
  DashboardHeader,
  SyncNotice,
  SaveNotice,
}: {
  state: ParentSyncState;
  values: {
    filename: string;
    path: string;
    mime_type: string;
    category: string;
    alt_text: string;
    description: string;
    tags: string;
  };
  errors: Record<string, string>;
  saveState: 'idle' | 'saving' | 'synced' | 'local' | 'error';
  saveError: string;
  hasSelectedFile: boolean;
  displayedRows: string[][];
  onChange: FormEventHandler<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>;
  onFileChange: FormEventHandler<HTMLInputElement>;
  onSubmit: FormEventHandler<HTMLFormElement>;
  DashboardHeader: DashboardHeaderComponent;
  SyncNotice: SyncNoticeComponent;
  SaveNotice: SaveNoticeComponent;
}) {
  const totalAssets = displayedRows.length;
  const categoryCount = new Set(displayedRows.map(([, category]) => category)).size;
  const needsAltText = displayedRows.filter((row) => row[3]?.toLowerCase().includes('alt')).length;
  const selectedRow = displayedRows[0] ?? [values.filename, values.category, values.description, 'Draft'];
  const assetCards = displayedRows.slice(0, 8);

  return (
    <section className="media-cms-page">
      <DashboardHeader title="Media Library" text="Manage images, avatars, quiz covers, badges, icons, and usage references." />
      <SyncNotice label="Media Library" state={state} />
      <div className="question-bank-metrics media-cms-metrics">
        <article><span>Total assets</span><strong>{totalAssets}</strong><em>Images in repository</em></article>
        <article><span>Categories</span><strong>{categoryCount}</strong><em>Website, avatars, badges</em></article>
        <article><span>Needs alt text</span><strong>{needsAltText}</strong><em>Accessibility review</em></article>
        <article className="accent"><span>Upload mode</span><strong>{hasSelectedFile ? 'File' : 'Metadata'}</strong><em>{hasSelectedFile ? 'New image selected' : 'No local file selected'}</em></article>
      </div>
      <section className="cms-filter-panel media-filter-panel">
        <label>
          Search
          <input disabled placeholder="Search assets by filename, tag, page, or section..." type="search" />
        </label>
        <label>
          Category
          <select disabled>
            <option>All categories</option>
            <option>Website</option>
            <option>Avatar</option>
            <option>Quiz cover</option>
            <option>Badge</option>
          </select>
        </label>
        <label>
          Status
          <select disabled>
            <option>All statuses</option>
            <option>Active</option>
            <option>Needs alt text</option>
          </select>
        </label>
        <span>Filters are visual in this phase; live media search comes after media detail routing.</span>
      </section>
      <div className="media-cms-layout">
        <section className="cms-panel media-grid-panel">
          <div className="cms-panel-heading">
            <div>
              <p className="eyebrow">Repository</p>
              <h2>Media assets</h2>
            </div>
            <span>{assetCards.length} shown</span>
          </div>
          <div className="media-asset-grid">
            {assetCards.map(([name, category, usage, status], index) => (
              <article className={index === 0 ? 'selected' : ''} key={`${name}-${category}`}>
                <div className="media-asset-thumb">
                  <span>{category.slice(0, 2).toUpperCase()}</span>
                </div>
                <div>
                  <strong>{name}</strong>
                  <span>{category}</span>
                  <em>{status}</em>
                </div>
                <p>{usage}</p>
              </article>
            ))}
          </div>
        </section>
        <aside className="media-cms-sidebar">
          <section className="media-detail-card">
            <div className="cms-panel-heading">
              <div>
                <p className="eyebrow">Selected Asset</p>
                <h2>Asset details</h2>
              </div>
            </div>
            <div className="media-selected-preview">
              <span>{selectedRow[1]?.slice(0, 2).toUpperCase() || 'MD'}</span>
            </div>
            <dl>
              <div><dt>Filename</dt><dd>{selectedRow[0]}</dd></div>
              <div><dt>Usage type</dt><dd>{selectedRow[1]}</dd></div>
              <div><dt>Status</dt><dd>{selectedRow[3]}</dd></div>
              <div><dt>Reference</dt><dd>{selectedRow[2]}</dd></div>
            </dl>
          </section>
          <form className="upload-panel media-upload-card" onSubmit={onSubmit}>
            <div className="cms-panel-heading">
              <div>
                <p className="eyebrow">Upload</p>
                <h2>{hasSelectedFile ? 'Upload new file' : 'Save metadata'}</h2>
              </div>
            </div>
            <p>PNG, JPG, SVG, and WEBP assets are tracked with alt text, tags, and usage references.</p>
            <label className="media-dropzone">Image file<input accept="image/*" name="file" onChange={onFileChange} type="file" />{errors.file ? <span className="field-error">{errors.file}</span> : null}</label>
            <div className="form-grid cms-form-grid">
              <label>Filename<input name="filename" onChange={onChange} value={values.filename} />{errors.filename ? <span className="field-error">{errors.filename}</span> : null}</label>
              <label>Storage path<input name="path" onChange={onChange} value={values.path} />{errors.path ? <span className="field-error">{errors.path}</span> : null}</label>
              <label>Mime type<select name="mime_type" onChange={onChange} value={values.mime_type}><option value="image/webp">WEBP</option><option value="image/png">PNG</option><option value="image/jpeg">JPG</option><option value="image/svg+xml">SVG</option></select>{errors.mime_type ? <span className="field-error">{errors.mime_type}</span> : null}</label>
              <label>Category<select name="category" onChange={onChange} value={values.category}><option value="website">Website</option><option value="hero">Hero</option><option value="avatar">Avatar</option><option value="quiz_cover">Quiz cover</option><option value="badge">Badge</option><option value="learning_article">Learning article</option><option value="other">Other</option></select></label>
              <label className="wide">Alt text<input name="alt_text" onChange={onChange} value={values.alt_text} />{errors.alt_text ? <span className="field-error">{errors.alt_text}</span> : null}</label>
              <label className="wide">Description<textarea name="description" onChange={onChange} value={values.description} /></label>
              <label className="wide">Tags<input name="tags" onChange={onChange} value={values.tags} /></label>
            </div>
            <SaveNotice error={saveError} state={saveState} />
            <button className="button button-primary" disabled={saveState === 'saving'} type="submit">{saveState === 'saving' ? 'Saving...' : hasSelectedFile ? 'Upload Media' : 'Save Metadata'}</button>
          </form>
        </aside>
      </div>
    </section>
  );
}

export function QuestionBuilderView({
  loadState,
  values,
  errors,
  saveState,
  saveError,
  quizOptions,
  previewTo,
  savedQuestionLabels,
  onChange,
  onSubmit,
  DashboardHeader,
  SyncNotice,
  SaveNotice,
}: {
  loadState: ParentSyncState;
  values: Record<string, string>;
  errors: Record<string, string>;
  saveState: 'idle' | 'saving' | 'synced' | 'local' | 'error';
  saveError: string;
  quizOptions: Array<{ id: string; title: string }>;
  previewTo: string;
  savedQuestionLabels: string[];
  onChange: FormEventHandler<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>;
  onSubmit: FormEventHandler<HTMLFormElement>;
  DashboardHeader: DashboardHeaderComponent;
  SyncNotice: SyncNoticeComponent;
  SaveNotice: SaveNoticeComponent;
}) {
  const answers = [
    ['A', 'answer_a'],
    ['B', 'answer_b'],
    ['C', 'answer_c'],
    ['D', 'answer_d'],
  ] as const;
  const completedAnswers = answers.filter(([, name]) => values[name]?.trim()).length;
  const hasExplanation = values.explanation.trim().length >= 20;
  const hasPrompt = values.prompt.trim().length >= 10;
  const readyChecks = [
    ['Quiz selected', Boolean(values.quiz_id)],
    ['Question prompt', hasPrompt],
    ['Four answers', completedAnswers === 4],
    ['Correct answer', Boolean(values.correct_answer)],
    ['Explanation', hasExplanation],
  ] as const;
  const readyCount = readyChecks.filter(([, ready]) => ready).length;
  const selectedQuizTitle = quizOptions.find((quiz) => quiz.id === values.quiz_id)?.title ?? 'Select a quiz';

  return (
    <section className="cms-builder-page question-bank-page">
      <DashboardHeader title="Question Bank" text="Create child-safe quiz prompts, answers, hints, explanations, and review-ready question records." />
      <div className="question-bank-metrics">
        <article><span>Total questions</span><strong>{savedQuestionLabels.length || 0}</strong><em>Current session</em></article>
        <article><span>Ready checks</span><strong>{readyCount}/5</strong><em>{readyCount === 5 ? 'Ready to save' : 'Needs review'}</em></article>
        <article><span>Answer options</span><strong>{completedAnswers}/4</strong><em>Multiple choice</em></article>
        <article className="accent"><span>Selected quiz</span><strong>{selectedQuizTitle}</strong><em>{values.is_active === 'true' ? 'Active question' : 'Inactive question'}</em></article>
      </div>
      <div className="question-builder cms-question-builder">
        <form className="cms-builder-form question-editor-form" onSubmit={onSubmit}>
          <section className="cms-editor-section">
            <div className="cms-section-heading">
              <span>01</span>
              <div>
                <h2>Question Setup</h2>
                <p>Attach this question to a quiz and define ordering, language support, and child-facing prompt text.</p>
              </div>
            </div>
            <SyncNotice label="Quiz list" state={loadState} />
            <div className="form-grid cms-form-grid">
              <label>
                Quiz
                <select name="quiz_id" onChange={onChange} value={values.quiz_id}>
                  <option value="">Select quiz</option>
                  {quizOptions.map((quiz) => (
                    <option key={quiz.id} value={quiz.id}>{quiz.title}</option>
                  ))}
                </select>
                {errors.quiz_id ? <span className="field-error">{errors.quiz_id}</span> : null}
              </label>
              <label>Sort order<input name="sort_order" onChange={onChange} type="number" value={values.sort_order} />{errors.sort_order ? <span className="field-error">{errors.sort_order}</span> : null}</label>
              <label className="wide">Question text<input name="prompt" onChange={onChange} value={values.prompt} />{errors.prompt ? <span className="field-error">{errors.prompt}</span> : null}</label>
              <label>Arabic text<input name="arabic_text" onChange={onChange} value={values.arabic_text} /></label>
              <label>Status<select name="is_active" onChange={onChange} value={values.is_active}><option value="true">Active</option><option value="false">Inactive</option></select></label>
            </div>
          </section>

          <section className="cms-editor-section">
            <div className="cms-section-heading">
              <span>02</span>
              <div>
                <h2>Answer Options</h2>
                <p>Choose the correct answer and keep every option short enough for small screens.</p>
              </div>
            </div>
            <div className="answer-editor-grid cms-answer-grid">
              {answers.map(([label, name]) => (
                <label className={values.correct_answer === label ? 'correct' : ''} key={label}>
                  <span>Answer {label}</span>
                  <input name={name} onChange={onChange} value={values[name]} />
                  {errors[name] ? <span className="field-error">{errors[name]}</span> : null}
                </label>
              ))}
            </div>
            <label className="cms-correct-answer">Correct answer<select name="correct_answer" onChange={onChange} value={values.correct_answer}><option>A</option><option>B</option><option>C</option><option>D</option></select></label>
          </section>

          <section className="cms-editor-section">
            <div className="cms-section-heading">
              <span>03</span>
              <div>
                <h2>Learning Support</h2>
                <p>Hints and explanations are required for a production-ready kids learning experience.</p>
              </div>
            </div>
            <div className="form-grid cms-form-grid">
              <label className="wide">Hint<input name="hint" onChange={onChange} value={values.hint} /></label>
              <label className="wide">Explanation<textarea name="explanation" onChange={onChange} value={values.explanation} />{errors.explanation ? <span className="field-error">{errors.explanation}</span> : null}</label>
              <label className="wide">Fun fact<textarea name="fun_fact" onChange={onChange} value={values.fun_fact} /></label>
            </div>
            <SaveNotice error={saveError} state={saveState} />
            <div className="button-row">
              <Link className="button button-secondary" to="/kids/admin/quizzes/new">Back to Quiz</Link>
              <button className="button button-primary" disabled={saveState === 'saving'} type="submit">{saveState === 'saving' ? 'Saving...' : 'Save Question'}</button>
              <Link className="button button-secondary" to={previewTo}>Preview Question</Link>
            </div>
          </section>
        </form>

        <aside className="question-list-panel cms-question-panel">
          <div className="cms-panel-heading">
            <div>
              <p className="eyebrow">Review Queue</p>
              <h2>Question readiness</h2>
            </div>
          </div>
          <div className="cms-readiness-list">
            {readyChecks.map(([label, ready]) => (
              <span className={ready ? 'ready' : ''} key={label}>{label}</span>
            ))}
          </div>
          <div className="question-preview-card">
            <small>{selectedQuizTitle}</small>
            <strong>{values.prompt}</strong>
            <p>{values.hint || 'Add a hint to support younger learners.'}</p>
            <div>
              {answers.map(([label, name]) => (
                <span className={values.correct_answer === label ? 'selected' : ''} key={label}>{label}. {values[name] || 'Empty answer'}</span>
              ))}
            </div>
          </div>
          <div className="saved-question-list">
            <h3>Saved this session</h3>
            {savedQuestionLabels.length ? savedQuestionLabels.map((label) => (
              <span key={label}>{label}</span>
            )) : (
              <span>No saved questions in this session yet.</span>
            )}
          </div>
        </aside>
      </div>
    </section>
  );
}
